import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, withCtx as _withCtx, vModelRadio as _vModelRadio, createTextVNode as _createTextVNode, withModifiers as _withModifiers, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "bg-white rounded-4 shadow p-4 mb-5" }
const _hoisted_2 = { class: "col-form-label fw-bold mb-2" }
const _hoisted_3 = ["name", "placeholder"]
const _hoisted_4 = { class: "radio-container d-flex align-items-center" }
const _hoisted_5 = {
  class: "fs-7",
  for: "articlePublic"
}
const _hoisted_6 = ["value"]
const _hoisted_7 = {
  class: "fs-7",
  for: "articlePrivate"
}
const _hoisted_8 = ["value"]
const _hoisted_9 = { class: "radio-container d-flex align-items-center" }
const _hoisted_10 = {
  class: "fs-7",
  for: "autoPostEnable"
}
const _hoisted_11 = ["disabled", "value"]
const _hoisted_12 = {
  class: "fs-7",
  for: "autoPostDisable"
}
const _hoisted_13 = ["disabled", "value"]
const _hoisted_14 = { class: "d-flex justify-content-center" }

import {
  ARTICLE_AUTO_POST_FIELD_NAME,
  ARTICLE_CONTENT_FIELD_NAME,
  ARTICLE_CONTENT_MAX_LENGTH,
  ARTICLE_PUBLISH_STATUS_FIELD_NAME,
  ARTICLE_PUBLISHED_PRIVATE_VALUE,
  ARTICLE_PUBLISHED_PUBLIC_VALUE,
  ARTICLE_TAGS_FIELD_NAME,
  ARTICLE_TITLE_FIELD_NAME,
  ArticleCreateMode,
  AUTO_POST_DISABLE,
  AUTO_POST_ENABLE,
  breadcrumb,
  editorTrimBottomOnlyNewLine,
  formEntryRules,
  removeCursorMarkFromContent,
  removeEmptyTagFromContent,
  URL_ARTICLE_CREATE_CONFIRM_PATH,
  URL_ARTICLE_EDIT_CONFIRM_PATH,
  VALIDATOR_CONTAIN_NG_WORD,
} from '@/views/community-admin/community-site/constants'
import Breadcrumb from '@/components/breadcrumb/index.vue'
import { useRoute, useRouter } from 'vue-router'
import { translate } from '@/languages/i18n'
import ArticleCreateField from '@/views/community-admin/community-site/components/ArticleCreateField.vue'
import { DEFAULT_MIN_LENGTH, MAX_EDITOR_NUMBER_OF_IMAGE, MAX_LENGTH_TITLE_SEARCH, MAX_UPLOAD_FILE_SIZE_MB } from '@/constants/number'
import Button from '@/components/button/index.vue'
import { computed, ComputedRef, onMounted, ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import InputErrorMessage from '@/components/input-error-message/InputErrorMessage.vue'
import commonService from '@/services/common.service'
import { isEmpty } from 'lodash'
import { useStore } from 'vuex'
import { COMMUNITY_SITE_ADMIN_STORE } from '@/store/constants'
import Loading from '@/components/loading/index.vue'
import ArticleEditor from '@/views/community-admin/community-site/components/ArticleEditor.vue'
import DOMPurify from 'dompurify'
import EditorTagInput from '@/views/community-admin/community-site/components/EditorTagInput.vue'
import userPostService from '@/services/user-post.services'
import { textEditorSanitizeConfig } from '@/constants/validate'


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const route = useRoute()
const router = useRouter()
const store = useStore()

const initData = () => {
  return {
    [ARTICLE_TITLE_FIELD_NAME]: '',
    [ARTICLE_CONTENT_FIELD_NAME]: '',
    [ARTICLE_PUBLISH_STATUS_FIELD_NAME]: ARTICLE_PUBLISHED_PUBLIC_VALUE,
    [ARTICLE_AUTO_POST_FIELD_NAME]: AUTO_POST_ENABLE,
    [ARTICLE_TAGS_FIELD_NAME]: Object.freeze([]),
  }
}
const formData = ref(Object.assign({}, initData()))
/**
 * Combine with existed validation from v$ rules
 * update value after field blurred.
 */
const $externalResults = ref()
const loading = ref(false)
const tagInputRef = ref()
const editorController = ref()

const articleEditorContentDescription = () => {
  const descriptions = [
    translate('CK20ContentTextLimit').replace('%s', ARTICLE_CONTENT_MAX_LENGTH.toLocaleString('en-US')),
    translate('CK20ContentImageLimit').replace('%s', MAX_EDITOR_NUMBER_OF_IMAGE.toString(10)),
    translate('CK20ContentImageSizeLimit').replace('%s', MAX_UPLOAD_FILE_SIZE_MB.toString(10)),
    translate('CK20ContentImageFormat'),
  ]
  return descriptions.join('\n') ?? ''
}

const v$ = useVuelidate(
  computed(() => formEntryRules(imageMapping)),
  formData,
  { $externalResults }
)

const mode: ComputedRef<ArticleCreateMode> = computed(() => (route.params.idArticle ? ArticleCreateMode.EDIT : ArticleCreateMode.CREATE))
let imageMapping = {}

onMounted(() => {
  checkAndFetchArticleData()
  imageMapping = { ...store.getters[COMMUNITY_SITE_ADMIN_STORE.GETTERS.IMAGE_MAPPING] }
  editorController.value?.setImageMapping(imageMapping)
})

const handlePublishStatusPrivateClick = () => {
  formData.value[ARTICLE_AUTO_POST_FIELD_NAME] = AUTO_POST_DISABLE
}

const onContentTextChange = (p: string, mapping: any) => {
  formData.value[ARTICLE_CONTENT_FIELD_NAME] = p
  imageMapping = { ...imageMapping, ...mapping }
  $externalResults.value = { ...$externalResults.value, [ARTICLE_CONTENT_FIELD_NAME]: [] }
  v$.value[ARTICLE_CONTENT_FIELD_NAME].$touch()
}
const handleTriggerContentValidation = () => {
  v$.value[ARTICLE_CONTENT_FIELD_NAME].$touch()
}

const handleBack = () => {
  router.go(-1)
}

const onSubmit = async () => {
  if (tagInputRef.value) {
    const { tags, removedTags = [] } = await tagInputRef.value.getAllTagContent()
    formData.value[ARTICLE_TAGS_FIELD_NAME] = [
      ...tags,
      ...removedTags.map((tag) => {
        tag['_destroy'] = 1
        return tag
      }),
    ]
  }

  // Sanitize HTML
  let originalHtmlText = editorController.value.getContentWithFixedSizeImages()
  originalHtmlText = removeCursorMarkFromContent(originalHtmlText)
  // const { content } = editorTrimBottomOnlyNewLine(originalHtmlText)
  formData.value[ARTICLE_CONTENT_FIELD_NAME] = DOMPurify.sanitize(originalHtmlText, textEditorSanitizeConfig)

  // Remove unused image from image-mapping
  Object.keys(imageMapping).forEach((tempImage: string) => {
    if (!formData.value[ARTICLE_CONTENT_FIELD_NAME].includes(tempImage.replaceAll(/&/g, '&amp;'))) {
      delete imageMapping[tempImage]
    }
  })
  await store.dispatch(COMMUNITY_SITE_ADMIN_STORE.ACTIONS.SET_ARTICLE_DATA, { data: formData.value, imageMapping })
  const communityId = router.currentRoute.value.params.id?.toString() ?? ''
  if (mode.value === ArticleCreateMode.CREATE) {
    await router.push(URL_ARTICLE_CREATE_CONFIRM_PATH(communityId))
  } else {
    const articleId = router.currentRoute.value.params.idArticle?.toString() ?? ''
    await router.push(URL_ARTICLE_EDIT_CONFIRM_PATH(communityId, articleId))
  }
}

const checkAndFetchArticleData = async () => {
  const articleSavedData = store.getters[COMMUNITY_SITE_ADMIN_STORE.GETTERS.GET_ARTICLE]
  // Retain the submit data in cased of user from confirm page back to form page
  if (articleSavedData && articleSavedData[ARTICLE_TITLE_FIELD_NAME]) {
    Object.keys(articleSavedData).map((key: string) => {
      formData.value[key] = articleSavedData[key]
    })
    tagInputRef.value?.updateTags(articleSavedData[ARTICLE_TAGS_FIELD_NAME])
    editorController.value.setEditorContent(articleSavedData[ARTICLE_CONTENT_FIELD_NAME])
  }
  // In case of forward to create page directly from url or from list page
  // check mode from url and fetch suitable data
  else {
    if (mode.value === ArticleCreateMode.EDIT) {
      const idArticle = route.params.idArticle.toString()
      const idCommunity = route.params.id.toString()
      loading.value = true
      try {
        const response = await userPostService.detailUserPosts(idCommunity, idArticle)
        const { post } = response.data.data
        let postContent = post[ARTICLE_CONTENT_FIELD_NAME]
        postContent = removeCursorMarkFromContent(postContent)
        postContent = removeEmptyTagFromContent(postContent)
        formData.value = { ...post, [ARTICLE_CONTENT_FIELD_NAME]: postContent }
        tagInputRef.value.updateTags(post?.tags)
        editorController.value.setEditorContent(formData.value[ARTICLE_CONTENT_FIELD_NAME])
      } catch (e) {
        console.error(e)
      } finally {
        loading.value = false
      }
    }
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Breadcrumb, {
      breadcrumb: _unref(breadcrumb)(_unref(route).params?.id.toString(), _unref(mode)),
      class: "mx-0"
    }, null, 8, ["breadcrumb"]),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h5", _hoisted_2, _toDisplayString(_unref(mode) === _unref(ArticleCreateMode).EDIT ? _unref(translate)('CM10ArticleUpdate') : _unref(translate)('CK20ArticleCreation')), 1),
      _createElementVNode("form", null, [
        _createVNode(ArticleCreateField, {
          label: _unref(translate)('title')
        }, {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("input", {
              name: _unref(ARTICLE_TITLE_FIELD_NAME),
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$)[_unref(ARTICLE_TITLE_FIELD_NAME)].$model) = $event)),
              class: _normalizeClass(["form-control w-100", _unref(v$)[_unref(ARTICLE_TITLE_FIELD_NAME)].$error ? 'is-invalid' : '']),
              type: "text",
              placeholder: _unref(translate)('SD10requireTitle')
            }, null, 10, _hoisted_3), [
              [
                _vModelText,
                _unref(v$)[_unref(ARTICLE_TITLE_FIELD_NAME)].$model,
                void 0,
                { trim: true }
              ]
            ]),
            _createVNode(InputErrorMessage, {
              title: _unref(translate)('title'),
              params: [_unref(DEFAULT_MIN_LENGTH), _unref(MAX_LENGTH_TITLE_SEARCH)],
              validator: 
            _unref(v$)[_unref(ARTICLE_TITLE_FIELD_NAME)].$errors[0]?.$validator === '$externalResults'
              ? _unref(v$)[_unref(ARTICLE_TITLE_FIELD_NAME)].$errors[0].$message.toString()
              : _unref(v$)[_unref(ARTICLE_TITLE_FIELD_NAME)].$errors[0]?.$validator
          
            }, null, 8, ["title", "params", "validator"])
          ]),
          _: 1
        }, 8, ["label"]),
        _createVNode(ArticleCreateField, {
          label: _unref(translate)('content'),
          "label-describe": articleEditorContentDescription()
        }, {
          default: _withCtx(() => [
            _createVNode(ArticleEditor, {
              ref: (_value, _refs) => {
                _refs['editorController'] = _value
                editorController.value = _value
              },
              content: formData.value[_unref(ARTICLE_CONTENT_FIELD_NAME)],
              "on-content-change": onContentTextChange,
              errors: _unref(v$)[_unref(ARTICLE_CONTENT_FIELD_NAME)].$errors,
              "editor-mode": _unref(mode),
              onTriggerValidate: handleTriggerContentValidation
            }, null, 8, ["content", "errors", "editor-mode"])
          ]),
          _: 1
        }, 8, ["label", "label-describe"]),
        _createVNode(ArticleCreateField, {
          label: _unref(translate)('CT10tag'),
          "label-describe": _unref(translate)('CK20TagDescription'),
          fieldRequired: false
        }, {
          default: _withCtx(() => [
            _createVNode(EditorTagInput, {
              "original-tags": formData.value[_unref(ARTICLE_TAGS_FIELD_NAME)],
              ref: (_value, _refs) => {
                _refs['tagInputRef'] = _value
                tagInputRef.value = _value
              },
              mode: _unref(mode)
            }, null, 8, ["original-tags", "mode"])
          ]),
          _: 1
        }, 8, ["label", "label-describe"]),
        _createVNode(ArticleCreateField, {
          label: _unref(translate)('publicSettings')
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("label", _hoisted_5, [
                _withDirectives(_createElementVNode("input", {
                  class: "form-check-input",
                  type: "radio",
                  name: "publishStatus",
                  id: "articlePublic",
                  value: _unref(ARTICLE_PUBLISHED_PUBLIC_VALUE),
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$)[_unref(ARTICLE_PUBLISH_STATUS_FIELD_NAME)].$model) = $event))
                }, null, 8, _hoisted_6), [
                  [_vModelRadio, _unref(v$)[_unref(ARTICLE_PUBLISH_STATUS_FIELD_NAME)].$model]
                ]),
                _createTextVNode(" " + _toDisplayString(_unref(translate)('public')), 1)
              ]),
              _createElementVNode("label", _hoisted_7, [
                _withDirectives(_createElementVNode("input", {
                  class: "form-check-input",
                  type: "radio",
                  name: "publishStatus",
                  id: "articlePrivate",
                  value: _unref(ARTICLE_PUBLISHED_PRIVATE_VALUE),
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$)[_unref(ARTICLE_PUBLISH_STATUS_FIELD_NAME)].$model) = $event)),
                  onChange: handlePublishStatusPrivateClick
                }, null, 40, _hoisted_8), [
                  [_vModelRadio, _unref(v$)[_unref(ARTICLE_PUBLISH_STATUS_FIELD_NAME)].$model]
                ]),
                _createTextVNode(" " + _toDisplayString(_unref(translate)('private')), 1)
              ])
            ])
          ]),
          _: 1
        }, 8, ["label"]),
        _createVNode(ArticleCreateField, {
          label: _unref(translate)('CK20AutoPost'),
          "label-describe": _unref(translate)('CK20AutoPostDescription')
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("label", _hoisted_10, [
                _withDirectives(_createElementVNode("input", {
                  disabled: formData.value[_unref(ARTICLE_PUBLISH_STATUS_FIELD_NAME)] === _unref(ARTICLE_PUBLISHED_PRIVATE_VALUE),
                  class: "form-check-input",
                  type: "radio",
                  name: "autoPost",
                  id: "autoPostEnable",
                  value: _unref(AUTO_POST_ENABLE),
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$)[_unref(ARTICLE_AUTO_POST_FIELD_NAME)].$model) = $event))
                }, null, 8, _hoisted_11), [
                  [_vModelRadio, _unref(v$)[_unref(ARTICLE_AUTO_POST_FIELD_NAME)].$model]
                ]),
                _createTextVNode(" " + _toDisplayString(_unref(translate)('CK20AutoPostYes')), 1)
              ]),
              _createElementVNode("label", _hoisted_12, [
                _withDirectives(_createElementVNode("input", {
                  disabled: formData.value[_unref(ARTICLE_PUBLISH_STATUS_FIELD_NAME)] === _unref(ARTICLE_PUBLISHED_PRIVATE_VALUE),
                  class: "form-check-input",
                  type: "radio",
                  name: "autoPost",
                  id: "autoPostDisable",
                  value: _unref(AUTO_POST_DISABLE),
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(v$)[_unref(ARTICLE_AUTO_POST_FIELD_NAME)].$model) = $event))
                }, null, 8, _hoisted_13), [
                  [_vModelRadio, _unref(v$)[_unref(ARTICLE_AUTO_POST_FIELD_NAME)].$model]
                ]),
                _createTextVNode(" " + _toDisplayString(_unref(translate)('CK20AutoPostNo')), 1)
              ])
            ])
          ]),
          _: 1
        }, 8, ["label", "label-describe"]),
        _createElementVNode("div", _hoisted_14, [
          _createVNode(Button, {
            title: _unref(translate)('back'),
            type: 'secondary',
            onClick: handleBack,
            className: "fw-bold me-2"
          }, null, 8, ["title"]),
          _createVNode(Button, {
            title: _unref(translate)('confirm'),
            disabled: _unref(v$).$invalid || !_unref(isEmpty)(_unref(v$).$errors) || loading.value,
            loading: loading.value,
            onClick: _withModifiers(onSubmit, ["prevent"]),
            className: "fw-bold",
            type: "secondary"
          }, null, 8, ["title", "disabled", "loading", "onClick"])
        ])
      ])
    ]),
    _createVNode(Loading, { loading: loading.value }, null, 8, ["loading"])
  ], 64))
}
}

})